import React, { useState } from 'react';
import { Card, Stack, LoadingOverlay, Container } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import { masterAreaService } from 'services/dacum/masterAreaService';
import { MasterArea } from 'services/api';
import PageTitle from 'components/PageTitle';
import Toolbar from 'components/Toolbar';
import DataTables from 'components/DataTables';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import Dates from 'components/Dates';

const MasterAreaPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [currentPage] = useState(1);
  const [pageSize] = useState(10);

  const defaultSearch = new URLSearchParams(location.search).get('search') || '';

  const [opened, { open, close }] = useDisclosure();
  const [areaToDelete, setAreaToDelete] = useState<string | null>(null);

  const {
    data: areasData,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: ['masterAreas', defaultSearch, currentPage, pageSize],
    queryFn: () =>
      masterAreaService.getList({
        search: defaultSearch,
        page: currentPage,
        limit: pageSize
      }),
    refetchOnWindowFocus: false
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => masterAreaService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['masterAreas'] });
      close();
      showSuccessNotification({
        title: 'Deleted Successfully!',
        message: 'Area has been deleted successfully'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Failed to delete area',
        message: error?.message || 'Something went wrong please try again'
      });
    }
  });

  const columns = [
    {
      heading: 'Area Code',
      props: 'areaCode',
      sortable: true,
      active: true,
      id: 'areaCode'
    },
    {
      heading: 'Area Name',
      props: 'areaName',
      sortable: true,
      active: true,
      id: 'areaName'
    },
    {
      heading: 'Created Date',
      props: (row: MasterArea) => <Dates date={row.dateIn} dateFormat='dateTime' />,
      sortable: true,
      active: true,
      id: 'dateIn'
    },
    {
      heading: 'Action',
      props: 'action',
      active: true,
      id: 'action'
    }
  ];

  const handleView = (row: MasterArea) => {
    navigate(`/app/dacum/master-area/${row.areaID}`);
  };

  const handleEdit = (row: MasterArea) => {
    navigate(`/app/dacum/master-area/edit/${row.areaID}`);
  };

  const handleDelete = (row: MasterArea) => {
    setAreaToDelete(row.areaID);
    open();
  };

  const handleCreateClick = () => {
    navigate('/app/dacum/master-area/add');
  };

  const processDelete = async () => {
    if (areaToDelete) {
      await deleteMutation.mutateAsync(areaToDelete);
    }
  };

  return (
    <Container fluid>
      <ModalPopupConfirmationComponent
        buttonText='Delete'
        statusProcess='delete'
        opened={opened}
        open={open}
        close={close}
        onSubmit={processDelete}
        itemLabel='area'
      />

      <PageTitle
        title='Master Area'
        description='Create and manage learning areas. Define different areas of competency and learning domains for your DACUM programs.'
      />

      <Card shadow='xs' radius='md' mt={20} pos='relative'>
        <LoadingOverlay visible={isLoading || isFetching || deleteMutation.isPending} />

        <Toolbar
          showSearch
          showCreateButton
          searchPlaceholder='Search areas...'
          createButtonText='Add New Area'
          defaultSearchValue={defaultSearch}
          onCreate={handleCreateClick}
          showFilter={false}
        />

        <Stack mt={20}>
          <DataTables
            columns={columns}
            data={areasData?.data || []}
            loading={isLoading}
            isNumbering
            activeSize={pageSize}
            withPagination
            handleView={handleView}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            emptyMessageTitle='No areas found'
            emptyMessageDescription='Create your first area by clicking the Add New Area button'
          />
        </Stack>
      </Card>
    </Container>
  );
};

export default MasterAreaPage;
